// Timed fleet orders (G-67): departure countdown, arrival, Cancel, Share/Unshare (G-70) and
// Reinforce (P31) for attacks and raids.
import type { FleetOrder } from '@stars-and-ships/api-types';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { endpoints } from '../../api/endpoints';
import { reinforceLink } from '../../fleetTiming';
import { formatDuration, formatShips, MISSION_LABELS } from '../../labels';
import { Button, ErrorNotice } from '../ui';

export function ScheduledOrders({
  galaxyId,
  orders,
  name,
  now,
  onChanged,
  showReinforce = false,
  canShare,
}: {
  galaxyId: string;
  orders: FleetOrder[];
  name: (id: string | null | undefined) => string;
  now: number;
  /** Called after a cancel or share so the caller refreshes its queries. */
  onChanged: () => void;
  showReinforce?: boolean;
  /** The commander is in an alliance (sharing needs one, G-70). */
  canShare: boolean;
}) {
  const [error, setError] = useState<unknown>(null);
  const done = {
    onSuccess: () => {
      setError(null);
      onChanged();
    },
    onError: setError,
  };
  const cancel = useMutation({
    mutationFn: (id: string) => endpoints.cancelFleetOrder(galaxyId, id),
    ...done,
  });
  const share = useMutation({
    mutationFn: (o: FleetOrder) => endpoints.shareFleetOrder(galaxyId, o.id, !o.shared),
    ...done,
  });
  return (
    <>
      {error ? <ErrorNotice error={error} /> : null}
      <ul className="space-y-2">
        {orders.map((o) => {
          const attack = o.mission === 'attack' || o.mission === 'raid';
          const late = o.expectedArriveAt !== o.arriveAt;
          return (
            <li
              key={o.id}
              data-order={o.id}
              className="hud-cut-sm border border-line bg-void/60 p-3"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 text-sm">
                  <p className="text-text">
                    {MISSION_LABELS[o.mission]} → {name(o.targetSystemId)}
                    {o.shared ? <span className="text-info"> · shared</span> : null}
                  </p>
                  <p className="truncate text-xs text-muted">
                    from {name(o.originSystemId)} · {formatShips(o.ships)}
                  </p>
                  <p className="font-mono text-xs tabular-nums text-info">
                    departs in {formatDuration(Date.parse(o.departAt) - now)} · arrives{' '}
                    {new Date(o.expectedArriveAt).toLocaleString()}
                  </p>
                  {late ? (
                    <p className="text-xs text-danger">Late — the gate network changed.</p>
                  ) : null}
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1">
                  <Button
                    variant="ghost"
                    disabled={cancel.isPending}
                    onClick={() => cancel.mutate(o.id)}
                  >
                    Cancel
                  </Button>
                  {attack && canShare ? (
                    <Button
                      variant="ghost"
                      disabled={share.isPending}
                      onClick={() => share.mutate(o)}
                    >
                      {o.shared ? 'Unshare' : 'Share'}
                    </Button>
                  ) : null}
                  {attack && showReinforce ? (
                    <Link
                      className="text-xs text-info underline"
                      to={reinforceLink(galaxyId, {
                        targetSystemId: o.targetSystemId,
                        mission: o.mission === 'raid' ? 'raid' : 'attack',
                        arriveAt: o.expectedArriveAt,
                      })}
                    >
                      Reinforce {o.mission === 'raid' ? 'raid' : 'attack'}
                    </Link>
                  ) : null}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </>
  );
}
